import { emailService } from './emailService';
import { useUserStore } from '../stores/userStore';
import { useListingStore } from '../stores/listingStore';
import { useLeaseStore } from '../stores/leaseStore';
import { useMessageStore } from '../stores/messageStore';

export const notificationTriggers = {
  // User registration and verification
  async onUserRegistration(email: string) {
    const verificationLink = `https://subleasehero.com/verify-email?token=${generateVerificationToken()}`;
    await emailService.sendVerificationEmail(email, verificationLink);
  },

  async onEmailVerified(userId: string) {
    const { users } = useUserStore.getState();
    const user = users[userId];
    if (user) {
      await emailService.sendWelcomeEmail(user);
    }
  },

  // Messaging notifications
  async onNewMessage(conversationId: string, senderId: string, recipientId: string) {
    const { users } = useUserStore.getState();
    const { conversations } = useMessageStore.getState();
    
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;

    const sender = users[senderId];
    const recipient = users[recipientId];
    if (sender && recipient) {
      await emailService.sendNewMessageNotification(
        recipient,
        sender,
        conversation.listingTitle
      );
    }
  },

  // Application notifications
  async onApplicationSubmitted(requestId: string) {
    const { requests } = useLeaseStore.getState();
    const { listings } = useListingStore.getState();
    const { users } = useUserStore.getState();

    const request = requests.find(r => r.id === requestId);
    if (!request) return;

    const listing = listings.find(l => l.id === request.listingId);
    const owner = users[request.listing.ownerId];
    
    if (listing && owner) {
      await emailService.sendApplicationSubmittedNotification(owner, listing, request);
    }
  },

  async onApplicationApproved(requestId: string) {
    const { requests } = useLeaseStore.getState();
    const { listings } = useListingStore.getState();
    const { users } = useUserStore.getState();

    const request = requests.find(r => r.id === requestId);
    if (!request) return;

    const listing = listings.find(l => l.id === request.listingId);
    const applicant = users[request.applicant.id];
    
    if (listing && applicant) {
      await emailService.sendApplicationApprovedNotification(applicant, listing);
    }
  },

  async onApplicationRejected(requestId: string) {
    const { requests } = useLeaseStore.getState();
    const { listings } = useListingStore.getState();
    const { users } = useUserStore.getState();

    const request = requests.find(r => r.id === requestId);
    if (!request) return;

    const listing = listings.find(l => l.id === request.listingId);
    const applicant = users[request.applicant.id];
    
    if (listing && applicant) {
      await emailService.sendApplicationRejectedNotification(applicant, listing);
    }
  },

  // Payment notifications
  async onDepositReceived(requestId: string, amount: number) {
    const { requests } = useLeaseStore.getState();
    const { listings } = useListingStore.getState();
    const { users } = useUserStore.getState();

    const request = requests.find(r => r.id === requestId);
    if (!request) return;

    const listing = listings.find(l => l.id === request.listingId);
    const applicant = users[request.applicant.id];
    const owner = users[request.listing.ownerId];
    
    if (listing && applicant && owner) {
      // Notify both parties
      await emailService.sendDepositReceivedNotification(applicant, listing, amount);
      await emailService.sendDepositReceivedNotification(owner, listing, amount);
    }
  },

  // Verification notifications
  async onVerificationApproved(userId: string, type: 'id' | 'address') {
    const { users } = useUserStore.getState();
    const user = users[userId];
    if (user) {
      await emailService.sendVerificationApprovedNotification(user, type);
    }
  },

  async onVerificationRejected(userId: string, type: 'id' | 'address', reason: string) {
    const { users } = useUserStore.getState();
    const user = users[userId];
    if (user) {
      await emailService.sendVerificationRejectedNotification(user, type, reason);
    }
  }
};

// Helper function to generate verification tokens
function generateVerificationToken(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}